import {
  DIVE_STAGE_DEFINITIONS,
  type DiveStage,
} from "../domain/guidedOperations.ts";
import type { PresenterDepth } from "../domain/governance.ts";
import type { ScenarioStage } from "../domain/incidents.ts";
import { selectDiveStage } from "../simulation/guidedOperationsSelectors.ts";
import type { PresenterAudience } from "./audience";
import type { GuidanceSurface } from "./fieldGuidance";

export type ResolvedDiveGuidance = {
  diveStage: DiveStage;
  label: string;
  objective: string;
  completionSignal: string;
  routePrompt: string;
  audiencePrompt: string;
  depthPrompt: string;
};

const routePrompts: Record<GuidanceSurface, Record<DiveStage, string>> = {
  operations: {
    diagnose: "Read the operating snapshot and connect human impact to the active service state.",
    integrate: "Connect the incident, change, switch, endpoint, and accountable owner.",
    validate: "Review readiness evidence and the human decision before service changes.",
    enable: "Verify recovery, preserve receipts, and route follow-up work.",
  },
  incidents: {
    diagnose: "Establish incident scope and link the strongest evidence.",
    integrate: "Connect evidence, infrastructure, ownership, and response consequences.",
    validate: "Resolve blocked checks and present approve and reject choices equally.",
    enable: "Verify service and endpoint recovery before closure.",
  },
  assets: {
    diagnose: "Identify which managed endpoint is affected and where it belongs.",
    integrate: "Connect custody, location, service, and incident relationships.",
    validate: "Confirm the asset record supports the proposed response.",
    enable: "Preserve assignment and verification evidence for follow-up work.",
  },
  "phone-detail": {
    diagnose: "Separate endpoint state from upstream voice and network impact.",
    integrate: "Connect the phone, assignment, jack, VLAN, and service path.",
    validate: "Confirm registration evidence before declaring recovery.",
    enable: "Record TEL-0318 re-registration and the accountable verification.",
  },
  infrastructure: {
    diagnose: "Locate the first unhealthy hop in the selected relationship path.",
    integrate: "Connect physical, logical, service, and person-impact edges.",
    validate: "Confirm each response claim against a stable path and evidence source.",
    enable: "Use the verified path to explain recovery and prevention work.",
  },
  reports: {
    diagnose: "Confirm the report scope matches the incident and affected services.",
    integrate: "Connect decisions, receipts, people, assets, and follow-up work.",
    validate: "Check that approval and recovery claims have accountable evidence.",
    enable: "Generate closure artifacts without implying external delivery.",
  },
  placeholder: {
    diagnose: "Identify what this product area can truthfully support now.",
    integrate: "Connect the planned capability to an implemented local workflow.",
    validate: "Keep unavailable integrations and records explicitly bounded.",
    enable: "Route the user to the next working task or documented follow-up.",
  },
};

const audiencePrompts: Record<PresenterAudience, string> = {
  "telecom-admin": "Prioritize continuity, ownership, voice/network impact, and safe recovery.",
  "it-staff": "Prioritize executable records, dependencies, evidence, and handoff detail.",
  executive: "Prioritize continuity, population impact, risk, and accountable decisions.",
  student: "Use privacy-safe language about availability and the campus experience.",
};

const depthPrompts: Record<PresenterDepth, string> = {
  overview: "Lead with outcome, ownership, and one next safe action.",
  operations: "Show the evidence, control, consequence, and receipt needed to act.",
  architecture: "Expose stable IDs, relationships, and provenance without changing access.",
};

export function resolveDiveGuidance(input: {
  surface: GuidanceSurface;
  audience: PresenterAudience;
  depth: PresenterDepth;
  scenarioStage: ScenarioStage;
}): ResolvedDiveGuidance {
  const diveStage = selectDiveStage(input.scenarioStage);
  const definition = DIVE_STAGE_DEFINITIONS[diveStage];
  return {
    diveStage,
    label: definition.label,
    objective: definition.objective,
    completionSignal: definition.completionSignal,
    routePrompt: routePrompts[input.surface][diveStage],
    audiencePrompt: audiencePrompts[input.audience],
    depthPrompt: depthPrompts[input.depth],
  };
}
